/**
 * Submit: one button for what Upload, Export and Continue used to do separately.
 *
 * Every record climbs as far as it can, and the scout is told the highest level reached:
 *
 *   saved     - in local storage: the saved copy (search menu) and the queue (Relay, Pi)
 *   exported  - also on the Pi, or in this laptop's file on the USB folder
 *   uploaded  - also in Firebase
 *
 * Upload is only tried with internet, and failing it is not an error - the record is
 * already saved and queued, and the Relay tab sends it later. The only error is a match
 * that cannot be saved correctly, which the pages check before calling this.
 * See apps/octopus/docs/sync-plan.md, Step 0.
 */
import Toast from './toast';
import { uploadObject, uploadTimekeepData } from './firebase';
import { enqueueRecord, enqueueTimekeeper, markFirebaseUploaded } from './queue';
import { checkHealth, getAcceptedIds, isOctopusConfigured, uploadPending } from './octopus';
import { writeQueueToUsb } from './usb';
import { SUBMISSION_ID_KEY } from './submissionId';

export const LEVELS = ['saved', 'exported', 'uploaded'];

/** Settles to {ok, reason?}; a step that throws is just a step that didn't happen. */
async function attempt(step) {
    if (!step) return { ok: false };
    try {
        const result = await step();
        return typeof result === 'object' && result !== null ? result : { ok: Boolean(result) };
    } catch (err) {
        return { ok: false, reason: err && err.message ? err.message : String(err) };
    }
}

/**
 * The order of a submit, with every side effect passed in.
 *
 * @param {object} steps
 * @param {() => void} steps.save              local storage; throwing here is the one error
 * @param {Object<string, () => Promise>} [steps.exports]  destination name -> send
 * @param {() => Promise} [steps.upload]       omitted when there is no internet
 * @param {string} [steps.uploadSkipped]       why upload was not tried, if it wasn't
 * @returns {Promise<{level: string, exportedTo: string[], uploadNote?: string, reason?: string}>}
 */
export async function runSubmit({ save, exports = {}, upload, uploadSkipped }) {
    try {
        save();
    } catch (err) {
        return { level: 'error', exportedTo: [], reason: `Could not save on this device: ${err && err.message ? err.message : err}` };
    }

    const names = Object.keys(exports);
    const [uploaded, ...exportResults] = await Promise.all([
        upload ? attempt(upload) : Promise.resolve({ ok: false }),
        ...names.map(name => attempt(exports[name])),
    ]);
    const exportedTo = names.filter((_, i) => exportResults[i].ok);

    let uploadNote;
    if (!uploaded.ok) uploadNote = upload ? (uploaded.reason || 'upload failed') : uploadSkipped;

    const level = uploaded.ok ? 'uploaded' : exportedTo.length ? 'exported' : 'saved';
    return { level, exportedTo, uploadNote };
}

/** "the Pi and USB" */
function joinNames(names) {
    return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The words for a result: "Uploaded", "Exported to the Pi", "Saved to local storage". */
export function submitMessage(result) {
    switch (result.level) {
        case 'uploaded':
            // Exports still happen with wifi (a flash drive copy too) - say so.
            return result.exportedTo?.length ? `Uploaded · also on ${joinNames(result.exportedTo)}` : 'Uploaded';
        case 'exported':
            return `Exported to ${joinNames(result.exportedTo)}`;
        case 'saved':
            return 'Saved to local storage';
        default:
            return result.reason || 'Not saved';
    }
}

/** One toast, the colour of the highest level reached. */
export function showSubmitResult(result, funnySounds, sfxEnabled) {
    const message = submitMessage(result);
    switch (result.level) {
        case 'uploaded': Toast.uploaded(message); break;
        case 'exported': Toast.exported(message); break;
        case 'saved': Toast.saved(message); break;
        default: Toast.error(message, funnySounds, sfxEnabled);
    }
}

/**
 * How long Submit waits to hear from the Pi. A Pi on the switch answers in milliseconds;
 * an unplugged one would otherwise hold the scout for the full 5 s health timeout. Giving
 * up early loses nothing - the record is queued and the status chip's sync sends it.
 */
export const SUBMIT_PI_WAIT_MS = 1500;

/** The Pi, if one is set and answering; true once it holds this record. */
function piStep(eventPath, id, baseName) {
    if (!isOctopusConfigured() || !eventPath) return undefined;
    return async () => {
        const health = await checkHealth(fetch, SUBMIT_PI_WAIT_MS);
        if (!health.reachable) return { ok: false, reason: health.reason };
        await uploadPending(eventPath, fetch, baseName);
        return { ok: getAcceptedIds(eventPath, baseName).has(id) };
    };
}

/**
 * Submit a scouted match. The page has already validated it and checked `uploadBlocker`.
 *
 * @param {object} args
 * @param {object} args.record     formatData output, carrying its submission_id
 * @param {() => void} args.saveCopy   writes the search-menu copy
 * @param {number} args.gamemode
 * @param {string} args.eventPath
 * @param {string|null} args.uploadBlocked  why Firebase can't be reached now, or null
 * @param {Promise<boolean>} [args.usbReady]  primeUsbPermission() (Submit) or the folder picker (Export), started during the tap
 * @param {boolean} [args.usbOnly]  the Export button: save + the USB folder, no Pi, no upload
 */
export async function submitMatch({ record, saveCopy, gamemode, eventPath, uploadBlocked, usbReady, usbOnly = false }) {
    const id = record[SUBMISSION_ID_KEY];
    const exports = {};
    const pi = usbOnly ? undefined : piStep(eventPath, id, 'scouting');
    if (pi) exports['the Pi'] = pi;
    if (usbOnly) uploadBlocked = 'export only';
    if (usbReady) exports.USB = async () => (await usbReady) && writeQueueToUsb(eventPath);

    return runSubmit({
        save: () => {
            saveCopy();
            enqueueRecord(record, gamemode, eventPath);
        },
        exports,
        upload: uploadBlocked ? undefined : async () => {
            const ok = await uploadObject({ ...record }, eventPath, gamemode, { quiet: true });
            if (ok) markFirebaseUploaded([id], eventPath);
            return ok;
        },
        uploadSkipped: uploadBlocked || undefined,
    });
}

/**
 * Submit Timekeeper timestamps. Same levels; the USB file carries only the scouting
 * queue (Relay's Import Folder files everything it reads as scouting), so its export
 * is the Pi alone.
 */
export async function submitTimekeeper({ record, saveCopy, gamemode, eventPath, uploadBlocked }) {
    const id = record[SUBMISSION_ID_KEY] || crypto.randomUUID();
    const withId = { ...record, [SUBMISSION_ID_KEY]: id };
    const { [SUBMISSION_ID_KEY]: _omit, ...forFirebase } = withId;
    const exports = {};
    const pi = piStep(eventPath, id, 'timekeeper');
    if (pi) exports['the Pi'] = pi;

    return runSubmit({
        save: () => {
            saveCopy();
            enqueueTimekeeper(withId, gamemode, eventPath);
        },
        exports,
        upload: uploadBlocked ? undefined : async () => {
            const ok = await uploadTimekeepData(forFirebase, gamemode, eventPath, { quiet: true });
            if (ok) markFirebaseUploaded([id], eventPath);
            return ok;
        },
        uploadSkipped: uploadBlocked || undefined,
    });
}
